/* eslint-disable max-len, jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Link from 'next/link';
import { ChevronRight, Phone } from 'lucide-react'; // Phone used in desktop button only
import { cn } from '@/util/cn';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { SubNav, type SubNavItem } from '@/components/common/SubNav';
import { PartnerVideo } from '@/components/resort/PartnerVideo';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Shared header for resort-level production pages. Composes:
//   1. Breadcrumb (Country › Region › Resort)
//   2. H1 — the `heading` the caller supplies (e.g. "Vail Mountain Webcams")
//   3. Optional Nearby chip row — hidden on mobile
//   4. Optional OnTheSnow+ Partner block (`sponsored` prop). Ribbon,
//      Website / Phone buttons, and — for partners that have one — the
//      promo video thumbnail lapping the ribbon's tail. Renders above
//      the title on mobile, where the buttons stack in the narrow column
//      left of the thumbnail, or, with no thumbnail to make room for,
//      sit on one line under a centred ribbon; to the right of the title
//      at lg+, where they are side by side either way.
//   5. SubNav tabs — full-bleed on mobile, pill-capsule on md+
//
// `isSummerMode` repaints the season-dependent chrome — the breadcrumb
// links, the partner ribbon, the Website / Phone hovers, the nearby chips
// and the SubNav — from OnTheSnow blue/navy to the summer green ramp. The
// resort summer guide is the only page that sets it; ResortHeaderNav derives
// it from `isOnSummerOverview`.

export type ResortHeaderNearby = {
  name: string;
  href?: string;
};

export type ResortHeaderSponsored = {
  /** Resort website URL — opens in a new tab. */
  website?: string;
  /** Phone number; used as both the `tel:` href and the visible label. */
  phone?: string;
  /** YouTube URL for the resort's promo video. Rendered as a small poster
   *  thumbnail right of the partner block at every width; opens in a
   *  dialog on click. */
  video?: string;
};

export type ResortHeaderProps = {
  country: string;
  region: string;
  resortName: string;
  /**
   * The complete H1 text. Comes from the page's SEO hero title
   * (`hero.title` in the page's `.server.ts`), which carries the head term
   * the template ranks for — "Telluride Ski Resort", not "Telluride Guide".
   * Falls back to the bare resort name.
   */
  heading?: string;
  /** Optional nearby resort list rendered between H1 and SubNav. */
  nearby?: ResortHeaderNearby[];
  subNav: SubNavItem[];
  /** Link href for breadcrumb country + region segments. Defaults to '#'. */
  breadcrumbHref?: string;
  /** When set, renders the OnTheSnow+ Partner block. At least one of
   *  website/phone/video must be populated; otherwise the block hides. */
  sponsored?: ResortHeaderSponsored;
  /** Repaints the header's brand chrome in the summer green ramp. */
  isSummerMode?: boolean;
  className?: string;
};

// Season-dependent chrome, in design-system tokens only. Winter keeps the
// navy partner ribbon against the blue site header; summer keeps the same
// relationship by pairing `primary-summer-dark` against the green header,
// so the ribbon still reads as the heavier of the two.
const winterChrome = {
  ribbon: 'bg-secondary',
  accentBorder: 'hover:border-primary',
  accentText: 'hover:text-primary',
  icon: 'text-primary',
  chipHover: 'hover:border-primary/40 hover:bg-sky-50 hover:text-primary',
  crumbLink: 'text-primary hover:text-foreground',
};

const summerChrome = {
  ribbon: 'bg-primary-summer-dark',
  accentBorder: 'hover:border-primary-summer',
  accentText: 'hover:text-primary-summer',
  icon: 'text-primary-summer',
  chipHover: 'hover:border-primary-summer/40 hover:bg-primary-summer-surface hover:text-primary-summer',
  crumbLink: 'text-primary-summer hover:text-primary-summer-dark',
};

// py-1 + leading-tight leaves the buttons 23px tall below md, which is
// the height that squares the block off against the 96px poster beside
// it — but it is a small thing to hit with a thumb, and one of them
// dials a phone number. This grows each button's hit area 4px beyond
// its border on both sides without moving the pill it paints: 4px is
// exactly half the gap-2 between them, so the two areas meet and never
// overlap, and a tap near the seam still resolves to the button the
// viewer aimed at.
const TAP_TARGET = "before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']";

// The crumb links must carry their own text color. globals.scss styles the
// bare `a` element (`color: $primary`) in Tailwind's base layer, and an
// element-level color beats the `text-muted-foreground` this nav passes down
// by inheritance — so without a color utility on the anchor itself the crumbs
// come out OnTheSnow blue whatever the season around them.
const Crumbs = ({
  country, region, resortName, href = '#', isSummerMode,
}: {
  country: string;
  region: string;
  resortName: string;
  href?: string;
  isSummerMode?: boolean;
}) => {
  const chrome = isSummerMode ? summerChrome : winterChrome;
  return (
    <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted-foreground md:text-sm">
      <Link href={href} className={chrome.crumbLink}>{country}</Link>
      <ChevronRight size={14} className="fill-none text-muted-foreground/60" />
      <Link href={href} className={chrome.crumbLink}>{region}</Link>
      <ChevronRight size={14} className="fill-none text-muted-foreground/60" />
      <span className="font-medium text-foreground">{resortName}</span>
    </nav>
  );
};

const NearbyChips = ({ items, isSummerMode }: {
  items: ResortHeaderNearby[];
  isSummerMode?: boolean;
}) => {
  const { t } = useOTSTranslation('common');
  const chrome = isSummerMode ? summerChrome : winterChrome;
  return (
    <div className="mb-6 hidden flex-wrap items-center gap-2 md:flex">
      <Text variant="subtle" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('common.nearbyResorts')}
      </Text>
      {items.map((n) => (
        <Link
          key={n.name}
          href={n.href ?? '#'}
          className={cn(
            'inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-foreground transition',
            chrome.chipHover,
          )}
        >
          {n.name}
        </Link>
      ))}
    </div>
  );
};

// OnTheSnow+ Partner block. Ribbon + a compact Website / Phone button
// pair at every width; how the two sit against each other below lg
// depends on whether a promo thumbnail is sharing the row (see below).
const PartnerBlock = ({
  website, phone, video, isSummerMode,
}: ResortHeaderSponsored & { isSummerMode?: boolean }) => {
  const { t } = useOTSTranslation('common');
  const chrome = isSummerMode ? summerChrome : winterChrome;
  // The thumbnail overlaps this block's right edge at every width (-ml-4
  // below lg, lg:-ml-5), so the block must guarantee that what gets
  // covered is the ribbon and never the phone number. Two things make
  // that work:
  //   - at lg the ribbon takes lg:min-w-full so it spans the whole block
  //     and therefore always reaches under the thumbnail, whatever the
  //     translated label measures. Below lg it deliberately does not: the
  //     ribbon hugs its label instead, because a stretched bar is width
  //     the block cannot afford on a phone;
  //   - the button column reserves pr-6 (24px) / lg:pr-7 (28px) on its
  //     right, which must stay >= the overlap, so the buttons always stop
  //     short of the thumbnail — 8px short of it at both widths, since a
  //     reserve equal to the overlap leaves them touching. In en-US the ribbon and the phone button
  //     otherwise end within a pixel of each other, and which one is wider
  //     flips by locale, so this clearance is structural rather than a
  //     tuned width.
  // The overlap also has to beat the clipPath below, whose last 12px taper
  // to the point is empty space: an overlap of 12px or less is swallowed
  // entirely by the taper and reads as a gap rather than a bleed. That
  // taper is also why the overlap pays for itself on a narrow screen —
  // the 16px hidden under the thumbnail is 16px the ribbon no longer has
  // to find in the viewport.
  // All of it only when there is a thumbnail — otherwise the ribbon would
  // stretch past its text for no reason.
  const overlapsVideo = !!video;
  // Without a thumbnail the block is the only thing in a full-width row,
  // and every width decision above — the hugging ribbon, the stacked
  // buttons — was made to buy room for a poster that isn't there. So
  // below lg it spends that room instead: the card centres itself in the
  // row, and the buttons take the single line they never had space for
  // beside a poster. Left alone it reads as a narrow column pinned to
  // the left edge with the right half of the row empty.
  // At lg+ nothing changes either way: the block is already in the
  // title's right-hand column with its buttons side by side.
  const centeredOnMobile = !video;
  return (
    // Width is intentionally intrinsic at lg+ (no fixed w-*). The ribbon
    // and the button labels are all translated strings, so their combined
    // width varies per locale and any fixed width overflows in some of
    // them. That overflow used to be invisible because nothing sat to the
    // block's right; the promo thumbnail now does, and clipped it.
    // pt-1.5 drops the whole block ~5px so the ribbon doesn't sit
    // exactly tangent to the thumbnail's top edge. Padding on the block
    // rather than a margin on the ribbon keeps the ribbon-to-buttons
    // spacing intact, and leaves the thumbnail — a sibling flex item —
    // where it is.
    // w-fit below lg makes the ribbon size the block and the buttons
    // stretch to it. No min-w-0 with it: the automatic min-content floor
    // is what stops a squeezed ribbon spilling its label out of the flag
    // — it wraps inside the shape instead.
    <div
      className={cn(
        'w-fit pt-1.5 lg:w-auto lg:flex-none',
        // mx-auto centres the whole w-fit card in the row; text-center
        // then centres the ribbon inside it, which matters because the
        // block is sized by whichever of the two is wider and in en-US
        // they measure within a few pixels of each other — so either can
        // be the narrower one, and which it is flips by locale.
        centeredOnMobile && 'max-lg:mx-auto max-lg:text-center',
      )}
    >
      {/* Ribbon — a right-pointing flag with a notch cut into the left
          edge, flush to the block's left edge. Kept in normal flow so it
          contributes its width to the block above.
          Below lg the type is fluid rather than fixed. Measured in Inter,
          the en-US label plus pl-4 + pr-5 is 168px at 10px and 181px at
          11px, against 184px of room on a 320px screen once the
          thumbnail and its overlap are paid for. 11px clears that by 3px,
          which is no margin for a locale whose label runs longer, so the
          clamp spends the narrowest screens at 10px and reaches the full
          11px around 430px, where the room is not in question. From md
          the
          buttons take their own 12px, so the ribbon takes it too — a
          flag that reads smaller than the buttons it labels inverts the
          hierarchy, and by md the width is there for both.
          pl-4 is the notch's clearance: the shape cuts 10px into this
          edge, and less than 16px sits the label almost on the point. It
          had to taper on the narrowest screens while the poster was a
          fixed 112px wide; now that the poster narrows with the viewport,
          the room is there for the full 16px at every width.
          Deliberately no whitespace-nowrap — a longer translated label
          wraps to two lines (widest line ~119px, which fits everywhere)
          instead of overflowing the viewport. */}
      <div
        className={cn(
          'inline-flex w-fit max-w-full items-center py-1.5 pl-4 pr-5 text-[length:clamp(0.625rem,2.6vw,0.6875rem)] font-bold uppercase tracking-wider text-white shadow-md',
          'md:text-xs md:tracking-widest lg:pl-5 lg:pr-7',
          chrome.ribbon,
          overlapsVideo && 'lg:min-w-full',
        )}
        style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 10px 50%)' }}
      >
        {t('common.premierPartner')}
      </div>

      {/* pt-2 reproduces the 8px gap the old absolute ribbon left behind
          its 36px pt-9 offset; pr-6 / lg:pr-7 is the thumbnail clearance
          described above. */}
      <div className={cn('pt-2', overlapsVideo && 'pr-6 lg:pr-7')}>
        {/* Buttons — stacked below lg whenever the poster is beside
            them, so the pair costs the width of one button instead of
            two; side by side at lg+, and below lg as well once there is
            no poster to leave room for. Stacked, they stretch to the
            block the ribbon sized, so the flag and the buttons share an
            edge; on one line they size to their labels instead and
            centre under the flag. Mobile uses muted colors, 10px type
            and a tighter py-1 to feel like supporting metadata, not a
            primary CTA — and drops the phone icon for visual quiet.
            Desktop keeps the icon, the taller py-1.5 and a sharper
            border. */}
        {(website || phone) && (
          <div
            className={cn(
              'flex gap-2 max-md:text-[0.625rem]',
              // One line below lg only when the poster isn't taking the
              // width. flex-wrap is the fallback for a locale whose two
              // labels outrun a 320px screen — they drop to two centred
              // lines rather than overflowing, and the seam between them
              // is a gap-2 the tap areas share exactly as they do when
              // stacked.
              centeredOnMobile
                ? 'flex-row flex-wrap justify-center lg:flex-nowrap lg:justify-start'
                : 'flex-col lg:flex-row',
            )}
          >
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'relative whitespace-nowrap rounded-full border border-border/60 bg-background px-3 py-1 text-center font-bold uppercase leading-tight tracking-wider text-muted-foreground transition',
                  TAP_TARGET,
                  'md:border-border md:py-1.5 md:text-xs md:tracking-widest md:text-foreground lg:flex-none lg:text-[0.6875rem]',
                  chrome.accentBorder,
                  chrome.accentText,
                )}
              >
                {t('common.website')}
              </a>
            )}
            {phone && (
              <a
                href={`tel:${phone}`}
                className={cn(
                  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border/60 bg-background px-3 py-1 font-bold leading-tight tabular-nums text-muted-foreground transition',
                  TAP_TARGET,
                  'md:border-border md:py-1.5 md:text-xs md:text-foreground lg:flex-none lg:text-[0.6875rem]',
                  chrome.accentBorder,
                  chrome.accentText,
                )}
              >
                <Phone size={13} className={cn('hidden fill-none md:inline-block', chrome.icon)} />
                {phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ResortHeader = ({
  country,
  region,
  resortName,
  heading,
  nearby,
  subNav,
  breadcrumbHref,
  sponsored,
  isSummerMode,
  className,
}: ResortHeaderProps) => {
  // Empty sponsored objects (e.g. isPartner=true but no website/phone/
  // video) shouldn't render an orphan ribbon.
  const hasSponsored = !!(sponsored?.website || sponsored?.phone || sponsored?.video);

  return (
    <header className={cn('mb-2 max-md:mb-6', className)}>
      {/* Title row. On mobile the partner block sits above the title
          via order-first; at lg+ it moves to the right column. */}
      <div
        className={cn(
          'flex flex-col lg:flex-row lg:items-start lg:gap-8',
          hasSponsored && 'lg:justify-between',
        )}
      >
        {hasSponsored && (
          // One row at every width: block on the left, thumbnail to its
          // right with its top near the ribbon's, pulled back far enough
          // that the badge visibly runs under it. Source order says the
          // same thing, so no child needs an order-* flip and the tab
          // order follows what the eye sees.
          // The group hugs its content rather than filling the row, so on
          // a phone it reads as a compact partner card above the title,
          // on the same max-md:px-4 inset as the H1 beneath it.
          // lg:pb-6 keeps the thumbnail — the tallest thing in this
          // column, so it sets the header row's height — off the SubNav
          // that follows immediately after the row.
          <div className="order-first mb-6 flex flex-row items-start max-md:px-4 lg:order-last lg:mb-0 lg:px-0 lg:pb-6">
            <PartnerBlock {...sponsored!} isSummerMode={isSummerMode} />
            {sponsored?.video && (
              <PartnerVideo
                url={sponsored.video}
                // Pulled back over the ribbon's tail, with z-10 so it
                // paints over it, not under. 16px of overlap below lg and
                // 20px at lg+ — see PartnerBlock for why it is never less.
                className="relative z-10 -ml-4 lg:-ml-5"
              />
            )}
          </div>
        )}
        <div className="min-w-0 flex-1 max-md:px-4">
          <Crumbs country={country} region={region} resortName={resortName} href={breadcrumbHref} isSummerMode={isSummerMode} />
          <Heading
            level={1}
            className="mb-4 font-heading text-2xl text-secondary md:text-4xl lg:text-5xl"
          >
            {heading || resortName}
          </Heading>
          {nearby && nearby.length > 0 && <NearbyChips items={nearby} isSummerMode={isSummerMode} />}
        </div>
      </div>
      {/* SubNav stays inset by the parent container's horizontal padding —
          sits visually flush with the body content beneath it. */}
      <SubNav items={subNav} isSummerMode={isSummerMode} />
    </header>
  );
};
